import { FunctionComponent, useEffect } from 'react';
import { usePreloadedQuery, useQueryLoader, PreloadedQuery } from 'react-relay';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { getUserGeoInfo } from 'dibs-regional-info/src/regionalInfoHelpers';
import widgetLoader from 'dibs-widget-loader';
import { KLARNA_PDP_PRICE_LIMIT } from './constants';

import { isKlarnaEligibleQuery, IsKlarnaEligibleQueryType } from './queries/IsKlarnaEligibleQuery';

import { KlarnaOnSiteMessage_item$data as ItemType } from './__generated__/KlarnaOnSiteMessage_item.graphql';
import { KlarnaOnSiteMessage_sku$data as SkuType } from './__generated__/KlarnaOnSiteMessage_sku.graphql';

type CreditComponentProps = {
    isKlarnaEligibleQueryReference: PreloadedQuery<IsKlarnaEligibleQueryType>;
    dataKey: string;
    dataLocale: string;
    price?: string;
    quantity?: string;
    sku?: SkuType | null;
};

const KlarnaOnSiteMessageCreditComponent: FunctionComponent<CreditComponentProps> = ({
    isKlarnaEligibleQueryReference,
    dataKey,
    dataLocale,
    price,
    quantity,
}) => {
    const { viewer } = usePreloadedQuery<IsKlarnaEligibleQueryType>(
        isKlarnaEligibleQuery,
        isKlarnaEligibleQueryReference
    );
    const isKlarnaEligible = viewer.item?.isKlarnaEligible;

    useEffect(() => {
        if (isKlarnaEligible) {
            widgetLoader.load('klarnaOnSiteMessaging');

            if (typeof window !== 'undefined') {
                window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
                window.KlarnaOnsiteService.push({
                    eventName: 'refresh-placements',
                });
            }
        }
    }, [price, dataKey, dataLocale, quantity, isKlarnaEligible]);

    if (isKlarnaEligible) {
        return (
            <klarna-placement
                data-key={dataKey} // eslint-disable-line ferrum/invalid-prop-format
                data-locale={dataLocale} // eslint-disable-line ferrum/invalid-prop-format
                data-purchase-amount={price} // eslint-disable-line ferrum/invalid-prop-format
            />
        );
    } else {
        return null;
    }
};

type LoaderProps = {
    dataKey: string;
    dataLocale: string;
    quantity?: string;
    currency?: string;
    userId?: string | null;
    item?: ItemType | null;
    sku?: SkuType | null;
};

const KlarnaOnSiteMessageLoader: FunctionComponent<LoaderProps> = ({
    dataKey,
    dataLocale,
    quantity,
    currency,
    userId,
    item,
    sku,
}) => {
    const userCountry = getUserGeoInfo()?.countryCode;
    const [
        isKlarnaEligibleQueryReference,
        loadKlarnaOnSiteMessageQuery,
    ] = useQueryLoader<IsKlarnaEligibleQueryType>(isKlarnaEligibleQuery);
    const itemDisplayPrice = sku ? sku.displayPrice?.[0] : item?.displayPrice?.[0];
    const price = itemDisplayPrice?.convertedAmount;
    const fullPrice = price ? price * (quantity ? parseInt(quantity) : 1) : null;
    const klarnaPrice = fullPrice ? (fullPrice * 100).toFixed() : '';
    const isPriceEligible = fullPrice && fullPrice <= KLARNA_PDP_PRICE_LIMIT;

    useEffect(() => {
        if (userCountry === 'US' && isPriceEligible) {
            loadKlarnaOnSiteMessageQuery({
                itemId: item?.serviceId,
                userId: userId ? userId : null,
                userCurrency: currency,
                amount: fullPrice?.toString(),
                userCountry: userCountry,
            });
        }
    }, [
        loadKlarnaOnSiteMessageQuery,
        item,
        sku,
        userId,
        fullPrice,
        currency,
        userCountry,
        isPriceEligible,
    ]);

    //price should be less than 17000 on PDP for Klarna OSM.
    //there is a check in BE for price, but only for 20000
    if (
        ['credit-promotion-auto-size', 'credit-promotion-badge'].includes(dataKey) &&
        isKlarnaEligibleQueryReference &&
        isPriceEligible
    ) {
        return (
            <KlarnaOnSiteMessageCreditComponent
                isKlarnaEligibleQueryReference={isKlarnaEligibleQueryReference}
                dataKey={dataKey}
                dataLocale={dataLocale}
                price={klarnaPrice}
                sku={sku}
                quantity={quantity}
            />
        );
    } else {
        return null;
    }
};

export const KlarnaOnSiteMessage = createFragmentContainer(KlarnaOnSiteMessageLoader, {
    item: graphql`
        fragment KlarnaOnSiteMessage_item on Item
        @argumentDefinitions(
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
            userCountryCode: { type: "String", defaultValue: "" }
            priceBookName: { type: "String" }
        ) {
            serviceId
            displayPrice(
                isTrade: $isTrade
                buyerId: $userId
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            ) {
                convertedAmount(currency: USD)
            }
        }
    `,
    sku: graphql`
        fragment KlarnaOnSiteMessage_sku on ItemSkuType
        @argumentDefinitions(
            hasSkuId: { type: "Boolean", defaultValue: false }
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
            userCountryCode: { type: "String", defaultValue: "" }
            priceBookName: { type: "String" }
        ) {
            ... @include(if: $hasSkuId) {
                serviceId
                displayPrice(
                    isTrade: $isTrade
                    buyerId: $userId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                ) {
                    convertedAmount(currency: USD)
                }
            }
        }
    `,
});

export default KlarnaOnSiteMessage;
